import React, { useCallback } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import PencilRedSrc from '../assets/pencil-red.png';
import PencilBlueSrc from '../assets/pencil-blue.png';
import PencilYellowSrc from '../assets/pencil-yellow.png';
import PencilOrangeSrc from '../assets/pencil-orange.png';
import PencilPurpleSrc from '../assets/pencil-purple.png';
import PencilPinkSrc from '../assets/pencil-pink.png';
import PencilGreenSrc from '../assets/pencil-green.png';
import PencilLtPinkSrc from '../assets/pencil-ltpink.png';
import PencilBrownSrc from '../assets/pencil-brown.png';
import PencilBlackSrc from '../assets/pencil-black.png';

export enum PencilColor {
  Red = 'red',
  Blue = 'blue',
  Yellow = 'yellow',
  Orange = 'orange',
  Purple = 'purple',
  Pink = 'pink',
  Green = 'green',
  LtPink = 'ltpink',
  Brown = 'brown',
  Black = 'black',
}

const COLOURS: Record<PencilColor, string> = {
  [PencilColor.Red]: '#ff0000',
  [PencilColor.Blue]: '#0064ac',
  [PencilColor.Yellow]: '#fff500',
  [PencilColor.Orange]: '#eb6d00',
  [PencilColor.Purple]: '#db00ff',
  [PencilColor.Pink]: '#ff00a8',
  [PencilColor.Green]: '#1ccd19',
  [PencilColor.LtPink]: '#ffc5d3',
  [PencilColor.Brown]: '#604000',
  [PencilColor.Black]: '#000000',
};

interface PencilProps {
  className?: string;
  color: PencilColor;
  currentColor?: PencilColor;
  setCurrentColor?: (color: PencilColor) => void;
}

const Pencil = ({
  className,
  color,
  currentColor,
  setCurrentColor,
}: PencilProps) => {
  const getSrc = useCallback((color: PencilColor) => {
    switch (color) {
      case PencilColor.Red:
        return PencilRedSrc;
      case PencilColor.Blue:
        return PencilBlueSrc;
      case PencilColor.Yellow:
        return PencilYellowSrc;
      case PencilColor.Orange:
        return PencilOrangeSrc;
      case PencilColor.Purple:
        return PencilPurpleSrc;
      case PencilColor.Pink:
        return PencilPinkSrc;
      case PencilColor.Green:
        return PencilGreenSrc;
      case PencilColor.LtPink:
        return PencilLtPinkSrc;
      case PencilColor.Brown:
        return PencilBrownSrc;
      case PencilColor.Black:
        return PencilBlackSrc;
      default:
        return null;
    }
  }, []);

  const src = getSrc(color);

  return (
    src && (
      <button
        className="relative group"
        onClick={() => setCurrentColor?.(color)}
      >
        <Image
          src={src}
          alt={`${color} pencil`}
          width={src.width}
          height={src.height}
          className={clsx(
            'relative w-12 h-auto',
            className,
            currentColor === color ? 'top-1' : 'top-6 group-hover:top-5',
          )}
        />
      </button>
    )
  );
};

interface ColorPickerProps {
  className?: string;
  currentColor: PencilColor;
  setCurrentColor: (color: PencilColor) => void;
}
export const ColorPicker = ({
  className,
  currentColor,
  setCurrentColor,
}: ColorPickerProps) => (
  <div className={clsx('flex gap-2', className)}>
    {Object.keys(COLOURS).map((color, index) => (
      <Pencil
        key={index}
        color={color as PencilColor}
        currentColor={currentColor}
        setCurrentColor={setCurrentColor}
      />
    ))}
  </div>
);
